import { useState } from 'react';
import { match } from 'ts-pattern';
import TooltipButton from '@/components/Button/TooltipButton';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import type { IconName } from '@/components/Icon/iconMap';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import useAppUserCreate from '../../hooks/useAppUserCreate';
import useAppUserDeactivate from '../../hooks/useAppUserDeactivate';
import useAppUserReactivate from '../../hooks/useAppUserReactivate';
import useLicenseGrant from '../../hooks/useLicenseGrant';
import useLicenseRevoke from '../../hooks/useLicenseRevoke';
import type { AppUser, AppUserGroupRef, GroupViolation, LicenseAlert } from '../../types';
import type { AppUserAction } from '../../types/action';
import { suggestedAction } from '../../types/action';
import type { RuleMatching } from '../../types/ruleMatch';
import { RULE_MATCHING } from '../../types/ruleMatch';
import { GroupProvisioningDialog } from '../GroupProvisioningDialog';
export type AppUserActionProps = {
  appIntegrationId: string;
  appUserId?: string;
  personId?: string;
  appName: string;
  action?: AppUserAction;
  onSuccess?: () => void;
  softwareId?: string;
  groupViolation?: GroupViolation;
  /** ライセンス付与 / 剥奪アクションの実行対象（NO_LICENSE / VIOLATED_LICENSE 行のみ） */
  licenseAlert?: LicenseAlert;
  /** 行のルール適合状態（要権限変更の行はグループ / ライセンス系アクションを disabled にする） */
  ruleMatching?: RuleMatching;
  user?: AppUser;
};

/** AppUserのRuleMatchに合わせてDressCodeが操作者に推奨するアクションを実行するためのボタンコンポーネント */
export const AppUserActionButton = ({
  appIntegrationId,
  appUserId,
  personId,
  appName,
  action,
  onSuccess,
  softwareId,
  groupViolation,
  licenseAlert,
  ruleMatching,
  user
}: AppUserActionProps) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'domain.appUser'
  });
  const {
    t: tPage
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.profile.app.users'
  });
  // ライセンス付与 / 剥奪はライセンスページの同アクションと文言を共有する
  const {
    t: tLicenseAction
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.assignments.licenseAction'
  });
  const tc = useCommonTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const createAppUser = useAppUserCreate({
    appIntegrationId,
    personId: personId || '',
    onSuccess
  });
  const reactivateAppUser = useAppUserReactivate({
    appIntegrationId,
    appUserId: appUserId || '',
    onSuccess
  });
  const deactivateAppUser = useAppUserDeactivate({
    appIntegrationId,
    appUserId: appUserId || '',
    onSuccess
  });
  const grantLicense = useLicenseGrant({
    softwareId: softwareId || '',
    appUserId: appUserId || '',
    licenseProductId: licenseAlert?.licenseProductId || '',
    onSuccess
  });
  const revokeLicense = useLicenseRevoke({
    softwareId: softwareId || '',
    appUserId: appUserId || '',
    licenseProductId: licenseAlert?.licenseProductId || '',
    onSuccess
  });
  const isLoading = createAppUser.isLoading || reactivateAppUser.isLoading || deactivateAppUser.isLoading || grantLicense.isLoading || revokeLicense.isLoading;
  const getButtonProps = () => match(action?.type).with(suggestedAction.CREATE, () => ({
    icon: 'UserRoundPlusIcon' as const satisfies IconName,
    text: t('component.AppUserActionButton.createUser'),
    tooltipKey: 'component.AppUserActionButton.toolTipCreateUserOnWebPage',
    dialogTitle: {
      resource: t('appUser'),
      action: tc('action.create')
    },
    onClick: () => {
      createAppUser.mutate();
    }
  })).with(suggestedAction.REACTIVATE, () => ({
    icon: 'UserRoundPlusIcon' as const satisfies IconName,
    text: t('component.AppUserActionButton.reactivateUser'),
    tooltipKey: 'component.AppUserActionButton.toolTipReactivateUserOnWebPage',
    dialogTitle: {
      resource: t('appUser'),
      action: tc('action.reactivate')
    },
    onClick: () => {
      reactivateAppUser.mutate();
    }
  })).with(suggestedAction.DEACTIVATE, () => ({
    icon: 'UserRoundMinusIcon' as const satisfies IconName,
    text: t('component.AppUserActionButton.deactivateUser'),
    tooltipKey: 'component.AppUserActionButton.toolTipDeactivateUserOnWebPage',
    dialogTitle: {
      resource: t('appUser'),
      action: tc('action.deactivate')
    },
    onClick: () => setIsDialogOpen(true)
  })).with(suggestedAction.GROUP_JOIN, () => ({
    icon: 'UserRoundPlusIcon' as const satisfies IconName,
    text: tPage('action.groupJoin'),
    tooltipKey: '',
    dialogTitle: {
      resource: '',
      action: ''
    },
    onClick: () => setIsGroupDialogOpen(true)
  })).with(suggestedAction.GROUP_LEAVE, () => ({
    icon: 'UserRoundMinusIcon' as const satisfies IconName,
    text: tPage('action.groupLeave'),
    tooltipKey: '',
    dialogTitle: {
      resource: '',
      action: ''
    },
    onClick: () => setIsGroupDialogOpen(true)
  })).with(suggestedAction.LICENSE_GRANT, () => ({
    icon: 'KeyRoundIcon' as const satisfies IconName,
    text: tLicenseAction('grant'),
    tooltipKey: '',
    dialogTitle: tLicenseAction('grantDialogTitle'),
    onClick: () => setIsDialogOpen(true)
  })).with(suggestedAction.LICENSE_REVOKE, () => ({
    icon: 'KeyRoundIcon' as const satisfies IconName,
    text: tLicenseAction('revoke'),
    tooltipKey: '',
    dialogTitle: tLicenseAction('revokeDialogTitle'),
    onClick: () => setIsDialogOpen(true)
  })).otherwise(() => null);
  const buttonProps = getButtonProps();
  if (!buttonProps) return null;

  // 確認ダイアログ（ConfirmActionDialog）経由で実行されるアクションのみ分岐する。
  // CREATE / REACTIVATE はボタン onClick で直接 mutate、GROUP 系は専用ダイアログのため対象外
  const handleAction = () => {
    switch (action?.type) {
      case suggestedAction.LICENSE_GRANT:
        grantLicense.mutate();
        break;
      case suggestedAction.LICENSE_REVOKE:
        revokeLicense.mutate();
        break;
      case suggestedAction.DEACTIVATE:
        deactivateAppUser.mutate();
        break;
      default:
        break;
    }
    setIsDialogOpen(false);
  };
  const getDescription = (action: AppUserAction): string => {
    if (action.type === suggestedAction.LICENSE_GRANT) {
      return tLicenseAction('grantDialogDescription');
    }
    if (action.type === suggestedAction.LICENSE_REVOKE) {
      return tLicenseAction('revokeDialogDescription');
    }
    if ('describe' in action) {
      return action.describe;
    }
    return t('component.AppUserActionButton.describe');
  };
  if (!action) {
    return <div>{t('component.AppUserActionButton.noRecommendedAction')}</div>;
  }
  const isGroupAction = action.type === suggestedAction.GROUP_JOIN || action.type === suggestedAction.GROUP_LEAVE;
  const isGroupActionDisabled = isGroupAction && (!softwareId || !user);
  const isLicenseAction = action.type === suggestedAction.LICENSE_GRANT || action.type === suggestedAction.LICENSE_REVOKE;

  // GWS（LICENSE 系アクションは GWS 型のみ表示）はアカウント発行＝ライセンス付与のため、
  // 個別の付与・剥奪 API が Google 側でエラーになる。BE でも action.disabled=true / API の
  // バリデーションを入れているが、SDK 対応までフロントでも一時的に無効化する
  // （解除時は BE のガードと合わせて戻すこと）
  const isLicenseActionDisabled = isLicenseAction;

  // 要権限変更（VIOLATED_ROLE）のアカウントはグループ / ライセンス系アクションを
  // 実行させず、理由をツールチップで示す（DC-8469。従来は action ごと落ちて
  // 「-」表示になり、なぜ実行できないのか分からなかった）
  const isBlockedByRoleViolation = (isGroupAction || isLicenseAction) && ruleMatching === RULE_MATCHING.VIOLATED_ROLE;
  const tooltipContent = (() => {
    if (isBlockedByRoleViolation) {
      return t('component.AppUserActionButton.toolTipBlockedByRoleViolation');
    }
    if (isLicenseAction && isLicenseActionDisabled) {
      // ライセンス付与・剥奪の一時無効化理由（BE の ManualLicenseOperationNotSupported と同旨）
      return t('component.AppUserActionButton.toolTipLicenseOperationNotSupported');
    }
    if (action.disabled && buttonProps.tooltipKey) {
      return t(buttonProps.tooltipKey, {
        defaultValue: '',
        name: appName
      });
    }
    return undefined;
  })();
  const groupDialogGroups: AppUserGroupRef[] = (() => {
    if (!groupViolation) return [];
    if (action.type === suggestedAction.GROUP_JOIN) return groupViolation.noGroup;
    if (action.type === suggestedAction.GROUP_LEAVE) return groupViolation.violatedGroup;
    return [];
  })();
  return <>
      <TooltipButton icon={buttonProps.icon} variant="outline" iconColor="primary" isLoading={isLoading} onClick={e => {
      e.stopPropagation();
      buttonProps.onClick();
    }} disabled={action.disabled || isLoading || isGroupActionDisabled || isLicenseActionDisabled || isBlockedByRoleViolation} content={tooltipContent}>
        {buttonProps.text}
      </TooltipButton>
      <ConfirmActionDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} onConfirm={handleAction} title={buttonProps.dialogTitle} description={getDescription(action)} />
      {isGroupAction && softwareId && user && <GroupProvisioningDialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen} softwareId={softwareId} appIntegrationId={appIntegrationId} appUser={user} groups={groupDialogGroups} mode={action.type === suggestedAction.GROUP_JOIN ? 'join' : 'leave'} />}
    </>;
};